import React, { useState, useRef, useEffect, useMemo } from "react";
import Button from "../Buttons/Button";
import FormFieldSet from "../FormFields/FormFieldSet";
import BarChart from "./BarChart/BarChart";
import AreaChart from "./AreaChart/AreaChart";
import TableChart from "./TableChart";
import DropdownMenuNew from "../Buttons/DropdownMenuNew";
import DrawerAdv from "../Modal/DrawerAdv";
// import * as XLSX from "xlsx";
import PieChart from "./PieChart";
import SmartTooltip from "../Info/SmartTooltip";

const ChartNew = ({
  type = "barchart", //barchart, areachart, tablechart
  title = "Title",
  height = "h-full",
  width = "w-full",
  rawData, // Array of objects (Works for all charts)
  chartLabel, // A key of an object from rawData (x-axis lables)
  chartValue, // A key of an object from rawData (chart values like bars, area chart values)
  barColors = [], // Bar colors array (primary,success,danger,warning,info,light,dark,invoiced,leave,draft)
  showHorizontalGrid, // Toggle displaying horizontal Grid in Bar and Area Charts
  showVerticalGrid, // Toggle displaying vertical Grid in Bar and Area Charts
  formatYAxisTick, // Format y arxis labels (ex: $ 100 or Rs 100)
  formatXAxisTick, // Format x arxis labels (ex: $ 100 or Rs 100)
  barChartProps = {
    steps: 5, // Number of y-axis lables (int)
    showValueLabel: true, // Toggle displaying value lable in bar and area chart
    barWidth: "48px",
    barRadius: "8px",
    backgroundColor: "bg-white",
    color: "primary", // bar color applies for all bars
    depth: 500, // Tailwind color depth, applies for all bars
  },
  areaChartProps = {
    steps: 5,
    showValueLabel: false,
    gradientFrom: "#E75D01", //Hexcode, RGB
    gradientTo: "#FF781F", //Hexcode, RGB
    strokeColor: "#f97316", //Hexcode, RGB
    isHover: true,
    backgroundColor: "bg-white",
    renderToolTip: () => <div>ToolTip</div>,
  },
  tableChartProps = {
    showSlNo: false,
    showCheckbox: false,
    hideToolBar: true,
    hideToolBarInNormalView: true,
    hideToolBarInExpandedView: true,
    tableTotalKey: "", //if there is single value pass as a string or if multiple, then pass as array of strings as ["",""]
    page: 1,
    limit: 10,
    totalCount: 0,
    accordion: null,
    serverPagination: false,
    noImage: false,
    defaultSelectedRows: [],
    onSelectionChange: () => {},
    onPageChange: () => {},
    onLimitChange: () => {},
    idKey: "id",
    hideIdColumn: true,
    hiddenFields: [],
    toolbarRight: {
      search: false,
      filter: false,
      download: false,
      add: [],
    },
  },
  chartToolbarRight = {
    date: false,
    filter: false,
    screenShare: false,
    upload: false,
    chartToggle: true,
    expand: true,
    dateValue: { from: null, to: null },
    selectDropdown: false,
    selectDropdownOptions: [],
    selectDropdownValue: null,
    onSelectDropdownChange: () => {},
    saveButton: false,
    onSaveClick: () => {},
    add: [],
    showInBothViews: false,
  },
  dateFilterKey, // A key of an object from rawData (filter data based on date)
  onToggleChart = () => {},
  expandedContent = null,
  expandedContentPosition = "bottom", // "top" or "bottom"
  onExportClick = () => {},
  onUploadClick = () => {},
  expandedMaxWidth = "max-w-3xl",
  onExpand = () => {},
  onCollapse = () => {},
  onDateChange,
  className = "",
  hideExpandedChart = false,
  expandedChartHeight = "h-full",
  expandedChartWidth = "w-full",
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [originalPosition, setOriginalPosition] = useState(null);
  const [dates, setDates] = useState(
    chartToolbarRight.dateValue || { from: null, to: null },
  );
  const wrapperRef = useRef(null);
  const [chartData, setChartData] = useState();
  const [chartType, setChartType] = useState(type);
  const [tableColumns, setTableColumns] = useState([]);
  const [filterModal, setFilterModal] = useState(false);
  const [filteredRawData, setFilteredRawData] = useState([]);
  const [hiddenXAxisValues, setHiddenXAxisValues] = useState([]);

  const handleExpand = () => {
    if (wrapperRef.current) {
      const rect = wrapperRef.current.getBoundingClientRect();
      const scrollTop =
        window.pageYOffset || document.documentElement.scrollTop;
      const scrollLeft =
        window.pageXOffset || document.documentElement.scrollLeft;

      setOriginalPosition({
        top: rect.top + scrollTop,
        left: rect.left + scrollLeft,
        width: rect.width,
        height: rect.height,
        scrollPosition: { x: scrollLeft, y: scrollTop },
      });
    }
    setIsExpanded(true);
    onExpand();
  };

  // const exportToExcel = () => {
  //   if (!filteredRawData || filteredRawData.length === 0) return;

  //   const ws = XLSX.utils.json_to_sheet(filteredRawData);
  //   const wb = XLSX.utils.book_new();
  //   XLSX.utils.book_append_sheet(wb, ws, "Sheet1");

  // const baseName = "table_data";
  // const timestamp = new Date().toISOString().split("T")[0];
  // const fileName = `${baseName}_${timestamp}.xlsx`;
  //   XLSX.writeFile(wb, fileName);
  // };

  const updateChartData = (data) => {
    const builtChartData = data.map((item, index) => {
      const colorObj =
        (barColors &&
          barColors.length > 0 &&
          barColors[index % barColors.length]) ||
        {};
      const colorData =
        colorObj && colorObj.depth !== undefined
          ? { color: colorObj.color, depth: colorObj.depth }
          : colorObj && colorObj.color
            ? { color: colorObj.color }
            : {};

      return {
        label: chartLabel ? item[chartLabel] : undefined,
        value: chartValue ? item[chartValue] : undefined,
        ...item,
        ...colorData,
      };
    });

    setChartData(builtChartData);
  };

  const handleDateChange = (value) => {
    setDates(value);
    if (onDateChange) onDateChange(value);
  };

  const handleCollapse = () => {
    setIsExpanded(false);
    onCollapse();

    setTimeout(() => {
      if (originalPosition) {
        window.scrollTo(
          originalPosition.scrollPosition.x,
          originalPosition.scrollPosition.y,
        );
      }
    }, 300);
  };

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === "Escape" && isExpanded) {
        handleCollapse();
      }
    };

    if (isExpanded) {
      document.addEventListener("keydown", handleEscape);
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }

    return () => {
      document.removeEventListener("keydown", handleEscape);
      document.body.style.overflow = "auto";
    };
  }, [isExpanded]);

  const uniqueXAxisLabels = useMemo(() => {
    if (!rawData || !chartLabel) return [];
    return [...new Set(rawData.map((item) => item[chartLabel]))];
  }, [rawData, chartLabel]);

  const toggleHiddenLabel = (label) => {
    setHiddenXAxisValues((prev) =>
      prev.includes(label) ? prev.filter((v) => v !== label) : [...prev, label],
    );
  };

  useEffect(() => {
    let filtered = rawData;

    if (filtered && filtered.length > 0) {
      if (hiddenXAxisValues && hiddenXAxisValues.length > 0 && chartLabel) {
        let filteredXaxis = rawData.filter(
          (item) => !hiddenXAxisValues.includes(item[chartLabel]),
        );
        filtered = filteredXaxis;
      }

      if (dateFilterKey && dates.from !== null) {
        const fromDate = new Date(dates.from);
        const toDate = dates.to === null ? fromDate : new Date(dates.to);

        const filteredDates = filtered.filter((item) => {
          const itemDate = new Date(item[dateFilterKey]);

          const parsedDate = isNaN(itemDate.getTime())
            ? new Date(Date.parse(item[dateFilterKey]))
            : itemDate;

          return parsedDate >= fromDate && parsedDate <= toDate;
        });
        filtered = filteredDates;
      }

      updateChartData(filtered);
      setFilteredRawData(filtered);
    }
  }, [hiddenXAxisValues, dates, rawData]);

  useEffect(() => {
    if (!rawData || rawData.length === 0) {
      setChartData([]);
      setTableColumns([]);
      return;
    }

    updateChartData(rawData);

    const cols = Object.keys(rawData[0]).map((key) => {
      // const isTotal = tableChartProps.tableTotalKey == key;
      const totalKeys = Array.isArray(tableChartProps.tableTotalKey)
        ? tableChartProps.tableTotalKey
        : [tableChartProps.tableTotalKey];
      const isTotal = totalKeys.includes(key);

      return {
        key,
        label: key,
        total: isTotal,
        ...(isTotal && {
          totalRender: (totalValue, totals) => <div>{totalValue}</div>,
        }),
      };
    });
    setTableColumns(cols);
  }, [rawData]);

  const renderChartToolbar = () => {
    return (
      <div className="flex items-center gap-2">
        {chartToolbarRight.saveButton && (
          <Button
            title={"save"}
            onClick={chartToolbarRight.onSaveClick}
            size="sm"
          />
        )}
        <div className="flex items-center gap-1">
          {chartToolbarRight?.add &&
            chartToolbarRight.add.map((item, key) => (
              <div key={`toolbar-add-${key}`}>
                {item.component ? (
                  item.component
                ) : (
                  <Button
                    icon={item?.icon}
                    type="icon"
                    variant="text"
                    color="black"
                    onClick={item?.onClick}
                  />
                )}
              </div>
            ))}
        </div>
        {chartToolbarRight.selectDropdown && (
          <div className="w-[100px]">
            <FormFieldSet
              type="select"
              noTitle
              placeholder="Select"
              size="sm"
              options={chartToolbarRight.selectDropdownOptions || []}
              value={
                chartToolbarRight.selectDropdownValue !== null &&
                chartToolbarRight.selectDropdownValue !== undefined
                  ? chartToolbarRight.selectDropdownValue
                  : undefined
              }
              onChange={(value) => {
                if (chartToolbarRight.onSelectDropdownChange) {
                  chartToolbarRight.onSelectDropdownChange(value);
                }
              }}
            />
          </div>
        )}
        {chartToolbarRight.date && (
          <FormFieldSet
            type="datepickerRange"
            placeholder="Select date range"
            size="sm"
            noTitle
            value={dates}
            onChange={handleDateChange}
          />
        )}
        {chartToolbarRight.filter && !isExpanded && (
          <Button
            type="icon"
            variant="text"
            icon={"filter_alt"}
            onClick={() => setFilterModal(true)}
            color="black"
          />
        )}
        {chartToolbarRight.screenShare && (
          // <DropdownMenuNew
          //   MenuSection={
          //     <div className="flex flex-col">
          //       <Button
          //         title="Excel"
          //         variant="text"
          //         size="xs-sm"
          //         className="!justify-start py-1"
          //         // onClick={exportToExcel}
          //         color="black"
          //       />
          //     </div>
          //   }
          // >
          <Button
            type="icon"
            variant="text"
            icon={"screen_share"}
            color="black"
            onClick={onExportClick}
          />
          // </DropdownMenuNew>
        )}
        {chartToolbarRight.upload && !isExpanded && (
          <SmartTooltip
            description={"Upload"}
            size="xs"
            color="black"
            noicon
            tooltipPosition="bottom"
            portal={true}
          >
            <Button
              type="icon"
              variant="text"
              icon={"upload"}
              color="black"
              onClick={onUploadClick}
            />
          </SmartTooltip>
        )}
        {chartToolbarRight.chartToggle && !isExpanded && (
          <DropdownMenuNew
            MenuSection={
              <div className="flex flex-col">
                {chartType !== "barchart" && (
                  <Button
                    title={"Bar Chart"}
                    variant="text"
                    color="black"
                    size="xs-sm"
                    className="!justify-start py-1"
                    onClick={() => {
                      setChartType("barchart");
                      onToggleChart("barChart");
                    }}
                  />
                )}
                {chartType !== "areachart" && (
                  <Button
                    title={"Area Chart"}
                    variant="text"
                    color="black"
                    size="xs-sm"
                    className="!justify-start py-1"
                    onClick={() => {
                      setChartType("areachart");
                      onToggleChart("barChart");
                    }}
                  />
                )}

                {chartType !== "tablechart" && (
                  <Button
                    title={"Table Chart"}
                    variant="text"
                    color="black"
                    size="xs-sm"
                    className="!justify-start py-1"
                    onClick={() => {
                      setChartType("tablechart");
                      onToggleChart("barChart");
                    }}
                  />
                )}
              </div>
            }
          >
            <SmartTooltip
              description={"Switch Graph"}
              size="xs"
              color="black"
              noicon
              tooltipPosition="bottom"
              portal={true}
            >
              <Button
                type="icon"
                variant="text"
                icon={"bar_chart"}
                color="black"
              />
            </SmartTooltip>
          </DropdownMenuNew>
        )}
        {chartToolbarRight.expand && !isExpanded && (
          <SmartTooltip
            description={"Expand"}
            size="xs"
            color="black"
            noicon
            tooltipPosition="bottom"
            portal={true}
          >
            <Button
              type="icon"
              variant="text"
              icon={"arrows_output"}
              onClick={handleExpand}
              color="black"
            />
          </SmartTooltip>
        )}
      </div>
    );
  };

  const newTableChartProps = () => {
    const shouldHideToolBar = isExpanded
      ? tableChartProps.hideToolBarInExpandedView &&
        chartToolbarRight.showInBothViews
      : tableChartProps.hideToolBarInNormalView;

    if (shouldHideToolBar) {
      return tableChartProps.toolbarRight;
    }

    return {
      ...tableChartProps.toolbarRight,
      add: [
        {
          component: renderChartToolbar(),
        },
      ],
    };
  };

  const renderField = () => {
    const shouldHideToolBar = isExpanded
      ? tableChartProps.hideToolBarInExpandedView
      : tableChartProps.hideToolBarInNormalView;

    switch (chartType) {
      case "barchart":
        return (
          <BarChart
            data={chartData}
            steps={barChartProps.steps}
            showHorizontalGrid={showHorizontalGrid}
            showVerticalGrid={showVerticalGrid}
            showValueLabel={barChartProps.showValueLabel}
            barWidth={barChartProps.barWidth}
            barRadius={barChartProps.barRadius}
            formatYAxisTick={formatYAxisTick}
            formatXAxisTick={formatXAxisTick}
            color={barChartProps.color}
            depth={barChartProps.depth}
            backgroundColor={barChartProps.backgroundColor}
          />
        );

      case "areachart":
        return (
          <AreaChart
            data={chartData}
            steps={areaChartProps.steps}
            showHorizontalGrid={showHorizontalGrid}
            showVerticalGrid={showVerticalGrid}
            showValueLabel={areaChartProps.showValueLabel}
            formatYAxisTick={formatYAxisTick}
            formatXAxisTick={formatXAxisTick}
            gradientFrom={areaChartProps.gradientFrom}
            gradientTo={areaChartProps.gradientTo}
            strokeColor={areaChartProps.strokeColor}
            backgroundColor={areaChartProps.backgroundColor}
            isHover={areaChartProps.isHover}
            renderToolTip={areaChartProps.renderToolTip}
          />
        );

      case "tablechart":
        return (
          <TableChart
            columns={tableColumns}
            rows={filteredRawData}
            hideToolBar={shouldHideToolBar}
            noImage={tableChartProps.noImage}
            showSlNo={tableChartProps.showSlNo}
            showCheckbox={tableChartProps.showCheckbox}
            serverPagination={tableChartProps.serverPagination}
            defaultSelectedRows={tableChartProps.defaultSelectedRows}
            onSelectionChange={tableChartProps.onSelectionChange}
            onPageChange={tableChartProps.onPageChange}
            onLimitChange={tableChartProps.onLimitChange}
            totalCount={tableChartProps.totalCount}
            hiddenFields={tableChartProps.hiddenFields}
            page={tableChartProps.page}
            limit={tableChartProps.limit}
            idKey={tableChartProps.idKey}
            hideIdColumn={tableChartProps.hideIdColumn}
            toolbarRight={
              !shouldHideToolBar
                ? newTableChartProps()
                : tableChartProps.toolbarRight
            }
          />
        );
      case "piechart":
        return <PieChart data={rawData} />;

      default:
        return null;
    }
  };

  const renderFilterData = () => {
    if (!rawData || rawData.length === 0) {
      return <div className="p-4 text-sm text-gray-500">No data to filter</div>;
    }

    return (
      <div className="flex flex-col">
        <div className="flex items-center justify-between px-2 py-2 border-b">
          <div className="text-sm font-medium">
            Toggle {chartLabel || "value"}
          </div>
        </div>

        <div className="flex flex-col">
          {uniqueXAxisLabels.map((label) => {
            const hidden = hiddenXAxisValues.includes(label);
            return (
              <div
                key={String(label)}
                className={`flex items-center justify-between border-b border-slate-200 py-2 px-2 transition-colors duration-150 ${
                  hidden ? "opacity-60" : "opacity-100"
                }`}
              >
                <div className="body-two">{String(label)}</div>
                <div>
                  <Button
                    type="icon"
                    variant="text"
                    size="xs-sm"
                    color={hidden ? "dark" : "primary"}
                    icon={hidden ? "visibility_off" : "visibility"}
                    onClick={() => toggleHiddenLabel(label)}
                  />
                </div>
              </div>
            );
          })}
        </div>
        <div className="flex justify-end items-center">
          <Button
            type="text"
            variant="text"
            title="Reset"
            onClick={() => setHiddenXAxisValues([])}
            className="!px-0"
          />
        </div>
      </div>
    );
  };

  const renderExpandedContent = () => (
    <div className="fixed inset-0 z-55 bg-white flex flex-col overflow-auto animate-fadeIn">
      {/* Close button and toolbar */}
      <div className="w-full flex justify-between items-center px-8 py-4 border-b">
        <h2 className="text-xl font-semibold">{title}</h2>
        <div className="flex items-center gap-4">
          {/* NEW: Render toolbar in expanded view header if not in table and showInBothViews is true */}
          {chartType !== "tablechart" &&
            chartToolbarRight.showInBothViews &&
            renderChartToolbar()}
          <Button
            type="icon"
            variant="text"
            color="black"
            icon={"arrows_input"}
            onClick={handleCollapse}
            size="xl"
          />
        </div>
      </div>

      <div
        className={`flex flex-col w-full ${
          expandedContentPosition === "top"
            ? "flex-col-reverse pb-4"
            : "flex-col pt-8"
        }`}
      >
        {/* Chart Section */}
        {!hideExpandedChart && (
          <div
            className={`flex justify-center self-center px-4 ${expandedChartHeight} ${expandedChartWidth}`}
          >
            {renderField()}
          </div>
        )}

        {/* Extra Expanded Content Section */}
        {expandedContent && (
          <div className="w-full px-4">{expandedContent}</div>
        )}
      </div>
    </div>
  );

  return isExpanded ? (
    renderExpandedContent()
  ) : (
    <div
      ref={wrapperRef}
      className={`${height} ${width} ${className} flex flex-col`}
    >
      <div
        className={`flex ${
          title.length > 0 ? `justify-between` : `justify-end`
        } items-center ${
          tableChartProps.hideToolBarInNormalView === false &&
          chartType === "tablechart"
            ? `mb-0`
            : `mb-2`
        } `}
      >
        <span className="text-md font-semibold text-gray-600 line-clamp-1 me-2">
          {title}
        </span>

        {tableChartProps.hideToolBarInNormalView === false &&
        chartType === "tablechart"
          ? ""
          : renderChartToolbar()}
      </div>

      {chartData && renderField()}

      <DrawerAdv isOpen={filterModal} onClose={() => setFilterModal(false)}>
        {renderFilterData()}
      </DrawerAdv>
    </div>
  );
};

export default ChartNew;
